/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { StyleSheet } from 'react-native-unistyles';
import { pd, Theme } from '@airasia-phoenix/react-native/tokens';

// Taken straight from Phoenix (the same cached instances `../utils` re-exports as
// `lightTheme`/`darkTheme`) so this module does not depend on the utils barrel:
// it is loaded first on the client (instrumentation-client) and in every jest
// setup, including packages that mock `@airasia-rn/ui/utils`.
const lightTheme = Theme.lightTheme;
const darkTheme = Theme.darkTheme;

export const breakpoints = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  superLarge: 1500,
  tvLike: 4000,
};

// The unistyles theme is the Phoenix theme under `pd`'s short names (`system` →
// `sys`, `reference` → `ref`). A stylesheet can then take the theme as `pd` and
// keep reading tokens exactly like the global:
//
//   StyleSheet.create((pd) => ({
//     box: { backgroundColor: getColor(pd.sys.colour.bg_primary) },
//   }));
//
// Reading the parameter (not the imported `pd` global) is what lets unistyles v3
// see the theme dependency and regenerate the style on a light/dark toggle.
export type AppTheme = {
  name: string;
  isDarkMode: boolean;
  sys: (typeof lightTheme)['system'];
  ref: (typeof lightTheme)['reference'];
  readonly comp: typeof pd.comp;
};

const toUnistylesTheme = (theme: typeof lightTheme): AppTheme =>
  Object.defineProperty(
    {
      name: theme.name,
      isDarkMode: theme.isDarkMode,
      sys: theme.system,
      ref: theme.reference,
    } as AppTheme,
    'comp',
    {
      // Component tokens are not part of a Phoenix Theme: `pd.comp` is rebuilt from
      // the active theme on every `pd.setTheme()`. Read it lazily so a stylesheet
      // re-run after a toggle gets the current values (capturing `pd.comp` here
      // would freeze the light ones into both themes). Relies on `pd.setTheme`
      // running before `UnistylesRuntime.setTheme` — see `updateTheme` in
      // AppConfigProvider.
      get: () => pd.comp,
      // Unistyles SSR serialises the whole configure() config into every HTML page
      // (window.__UNISTYLES_STATE__.config, never read back on the client). Keeping
      // `comp` non-enumerable keeps Phoenix's component-token tree (~120 kB) out.
      enumerable: false,
    },
  );

export type AppBreakpoints = typeof breakpoints;
export type AppThemes = {
  light: AppTheme;
  dark: AppTheme;
};

declare module 'react-native-unistyles' {
  export interface UnistylesBreakpoints extends AppBreakpoints {} // eslint-disable-line @typescript-eslint/no-empty-object-type
  export interface UnistylesThemes extends AppThemes {} // eslint-disable-line @typescript-eslint/no-empty-object-type
}

export function initThemes() {
  StyleSheet.configure({
    themes: {
      light: toUnistylesTheme(lightTheme),
      dark: toUnistylesTheme(darkTheme),
    },
    breakpoints,
    settings: {
      initialTheme: 'light',
      // Web: hand stylesheets the real theme object above instead of a copy whose
      // values are `var(--…)` strings. Phoenix colour tokens are class instances
      // (`toString()`, `toRgba()`, `withAlpha()`, alpha maths in `getColor`), which the
      // CSS-variable copy strips. It also keeps the per-element theme listeners, so
      // styles that read `theme` update on a light/dark toggle without a re-render.
      CSSVars: false,
    },
  });
}
